import {Button, Grid} from "@mui/material";
import "dayjs/locale/it";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormDateTimePicker} from "../../common/form-fields";
import {SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {LocationDiscountFormModel, LocationDiscountGetData} from "./Types/locationDiscount";
import {useLocationDiscountRepository} from "../../api/repositories/LocationDiscount";
import DealerAndTenant from "../../common/components/DealerAndTenant";

const LocationDiscountForm = () => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    // Use Form Context
    const {register, setValue, handleSubmit, watch, getValues} = useFormContext<LocationDiscountFormModel>();

    const [responseData, setResponseData] = useState<LocationDiscountGetData>()

    // Use Repository Hooks
    const {getLocationDiscountPaged, getLocationDiscountDatas} = useLocationDiscountRepository();

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const dealerOptions = dealerOption
        .filter(function (el) {
            return el.code !== "all";
        });

    const [locationModel, setLocationModel] = useState<SelectElementModelFE[]>([])
    const [profilesModel, setProfilesModel] = useState<SelectElementModelFE[]>([])
    const [discountsModel, setDiscountsModel] = useState<any>([])

    const getResponseData = async () => {
        const responseData: LocationDiscountGetData = await getLocationDiscountDatas(everyLegalEntitiesWithoutAll, dealerFilteredOptions)
        const location = (responseData as LocationDiscountGetData)?.locations.map(el => ({
            code: el.id,
            label: el.name
        })).sort((a, b) =>
            a.label > b.label ? 1 : -1,
        )
        setLocationModel([{code: 0, label: t("pricing:actions.selectAll")}, ...location]);
        const profiles = (responseData as LocationDiscountGetData)?.profiles?.map(el => ({
            code: el.id,
            label: el.name
        })).sort((a, b) =>
            a.label > b.label ? 1 : -1,
        )
        setProfilesModel([{code: 0, label: t("pricing:actions.selectAll")}, ...profiles]);
        const discounts = (responseData as LocationDiscountGetData)?.discountRange.map(el => el)
        setDiscountsModel([t("pricing:actions.selectAll"), ...discounts])
        setResponseData(responseData)
    }

    useEffect(() => {
        getResponseData()
    }, [])

    const onChangeLocation = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watch().prcLocation?.find((el: SelectElementModelFE) => el.code === 0)
                ? // applica il "selectAll
                locationModel.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                input.filter((el) => el.code !== 0);
        setValue("prcLocation", value);
    };

    const onChangeProfile = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watch().lppProfiles?.find((el: SelectElementModelFE) => el.code === 0)
                ? // applica il "selectAll
                profilesModel.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                input.filter((el) => el.code !== 0);
        setValue("lppProfiles", value);
    };

    const onChangeDiscount = (input: any) => {
        const value: any =
            // è stato cliccato "selectAll
            input.find((el: any) => el === t("pricing:actions.selectAll")) &&
            // tra i vecchi valori non c'è "selectAll
            !watch().discountList?.find((el: any) => el === t("pricing:actions.selectAll"))
                ? // applica il "selectAll
                discountsModel.filter((el: any) => el !== t("pricing:actions.selectAll"))
                : // setta solo i car group selezionati
                input.filter((el: any) => el !== t("pricing:actions.selectAll"));
        setValue("discountList", value);
    };

    return (
        <Grid container spacing={2} alignItems="start">
            <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                {/* LOCATION */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        multiple={true}
                        {...register(`prcLocation`)}
                        label={`Location*`}
                        options={locationModel || []}
                        isOptionEqualToValue={(
                            option: SelectElementModelFE,
                            value: SelectElementModelFE
                        ) => option.code === value.code}
                        handleChange={onChangeLocation}
                    />
                </Grid>
                {/* FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("validityDateFrom")}
                        label={`${t("pricing:ancillary.form.labels.reservationFrom")}*`}
                    />
                </Grid>
                {/* TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("validityDateTo")}
                        label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                        minDate={watch("validityDateFrom")}
                    />
                </Grid>
                {/* PICK UP FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("pickUpDateFrom")}
                        label={`${t("pricing:ancillary.form.labels.pickupDateFrom")}*`}
                    />
                </Grid>
                {/* PICK UP TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("pickUpDateTo")}
                        label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                        minDate={watch("pickUpDateFrom")}
                    />
                </Grid>
                {/* PROFILES */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        multiple={true}
                        {...register(`lppProfiles`)}
                        label={`${t("pricing:locationDiscount.profiles")}*`}
                        options={profilesModel || []}
                        isOptionEqualToValue={(
                            option: SelectElementModelFE,
                            value: SelectElementModelFE
                        ) => option.code === value.code}
                        handleChange={onChangeProfile}
                    />
                </Grid>
                {/* DISCOUNT */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        multiple={true}
                        {...register(`discountList`)}
                        label={`${t("pricing:locationDiscount.discount")}*`}
                        options={discountsModel || []}
                        getOptionLabel={(option) => option !== t("pricing:actions.selectAll") ? option + "%" : option}
                        isOptionEqualToValue={(
                            option: number,
                            value: number
                        ) => option === value}
                        handleChange={onChangeDiscount}
                    />
                </Grid>
                <DealerAndTenant
                    md={3}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                    dealerIsVisible={true}
                />
            </Grid>
            <Button sx={{display: "none"}} onClick={() => {
            }}/>
        </Grid>
    );
};

export default LocationDiscountForm;
